"use client";

import React from "react";
import EditSection, {
  TextEditor,
  ListItemEditor,
} from "../../sections/shared/EditSection";
import { SectionContentProps } from "../../types";
import { PiTrash, PiX } from "react-icons/pi";

export default function HokFooterEdit({
  content,
  onContentChange,
}: SectionContentProps) {
  const handleChange = (updated: Record<string, any>) => {
    onContentChange?.(updated);
  };

  return (
    <EditSection
      content={content}
      onContentChange={handleChange}
      contentFields={
        <>
          {/* Company Info */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Company</h4>
            <TextEditor
              label="Company Name"
              value={content.companyName}
              onChange={(v) => handleChange({ ...content, companyName: v })}
              placeholder="Hok"
            />
            <TextEditor
              label="Description"
              value={content.description}
              onChange={(v) => handleChange({ ...content, description: v })}
              placeholder="At Hok Themes, we specialize in crafting digital experiences..."
              multiline
            />
          </div>

          {/* Link Groups */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">NAVIGATION GROUPS</h4>

            <div className="space-y-3">
              {(content.linkGroups || []).map((group: any, groupIndex: number) => (
                <div key={groupIndex} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={group.title || ""}
                      onChange={(e) => {
                        const newGroups = [...(content.linkGroups || [])];
                        newGroups[groupIndex] = { ...group, title: e.target.value };
                        handleChange({ ...content, linkGroups: newGroups });
                      }}
                      placeholder="Group Title"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newGroups = (content.linkGroups || []).filter((_: any, idx: number) => idx !== groupIndex);
                        handleChange({ ...content, linkGroups: newGroups });
                      }}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                      title="Delete Group"
                    >
                      <PiTrash className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Links */}
                  <div className="space-y-2">
                    {(group.links || []).map((link: any, linkIndex: number) => (
                      <div key={linkIndex} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={link.label || ""}
                          onChange={(e) => {
                            const newGroups = [...(content.linkGroups || [])];
                            const newLinks = [...(group.links || [])];
                            newLinks[linkIndex] = { ...link, label: e.target.value };
                            newGroups[groupIndex] = { ...group, links: newLinks };
                            handleChange({ ...content, linkGroups: newGroups });
                          }}
                          placeholder="Link Label"
                          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                        <input
                          type="text"
                          value={link.href || ""}
                          onChange={(e) => {
                            const newGroups = [...(content.linkGroups || [])];
                            const newLinks = [...(group.links || [])];
                            newLinks[linkIndex] = { ...link, href: e.target.value };
                            newGroups[groupIndex] = { ...group, links: newLinks };
                            handleChange({ ...content, linkGroups: newGroups });
                          }}
                          placeholder="#"
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 text-center"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newGroups = [...(content.linkGroups || [])];
                            const newLinks = (group.links || []).filter((_: any, idx: number) => idx !== linkIndex);
                            newGroups[groupIndex] = { ...group, links: newLinks };
                            handleChange({ ...content, linkGroups: newGroups });
                          }}
                          className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                          title="Delete Link"
                        >
                          <PiX className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      onClick={() => {
                        const newGroups = [...(content.linkGroups || [])];
                        const newLinks = [...(group.links || []), { label: "", href: "#" }];
                        newGroups[groupIndex] = { ...group, links: newLinks };
                        handleChange({ ...content, linkGroups: newGroups });
                      }}
                      className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 mt-1 pl-1"
                    >
                      + Add Link
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() => {
                  const groups = content.linkGroups || [];
                  handleChange({
                    ...content,
                    linkGroups: [...groups, { title: "New Group", links: [] }],
                  });
                }}
                className="w-full py-2.5 text-xs text-blue-600 hover:text-blue-700 font-bold border border-dashed border-blue-300 rounded-lg bg-blue-50/50 hover:bg-blue-50 transition-colors flex items-center justify-center gap-1"
              >
                + Add Navigation Group
              </button>
            </div>
          </div>

          {/* Social Links */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Social Links</h4>
            <ListItemEditor
              label="Links"
              items={content.socialLinks || [
                { platform: "facebook", url: "https://www.facebook.com/" },
                { platform: "twitter", url: "https://www.twitter.com/" },
                { platform: "linkedin", url: "https://www.linkedin.com/" },
                { platform: "instagram", url: "https://www.instagram.com/" },
              ]}
              itemSchema={[
                { key: "platform", label: "Platform", type: "text" },
                { key: "url", label: "URL", type: "text" },
              ]}
              onChange={(items) => handleChange({ ...content, socialLinks: items })}
              addItemText="Add Social Link"
            />
          </div>

          {/* Copyright Settings */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Copyright Bar</h4>
            <TextEditor
              label="Copyright Text"
              value={content.copyrightText}
              onChange={(v) => handleChange({ ...content, copyrightText: v })}
              placeholder="© 2026 Instacks. All rights reserved."
            />
            
            <div className="space-y-2 mt-2">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Bottom Links</div>
              <ListItemEditor
                label="Footer Links"
                items={content.footerLinks || [
                  { label: "Support Center", href: "#" },
                  { label: "Terms of Service", href: "#" },
                  { label: "Privacy Policy", href: "#" }
                ]}
                itemSchema={[
                  { key: "label", label: "Link Label", type: "text" },
                  { key: "href", label: "URL", type: "text" }
                ]}
                onChange={(items) => handleChange({ ...content, footerLinks: items })}
                addItemText="Add Link"
              />
            </div>
          </div>
        </>
      }
    />
  );
}
